export type PowerPlant = {
  meterId: string;
  name: string;
  capacity: number;
  lat: string;
  lng: string;
  mediumVoltage: number | null;
  mediumCurrent: number;
  liveData: LiveData;
  liveDataInterval: number;
  recordSaveInterval: number;
  // Connectivity watchdog (server-side): the logger itself, or its device,
  // has been silent for longer than the plant's threshold.
  connectivityState?: "ok" | "pi_offline" | "device_offline" | null;
  connectivitySince?: string | null;
  lastContactAt?: string | null;
  startDate: string;
  timezone: string;
  relayChannelCount: number | null;
  showMarketPrices: boolean;
  canEdit: boolean;
  canManageAccess: boolean;
};

export type RecordSource = "meter" | "inverter";

// Inverter-only fields. Mirrors backend InverterExtras — present only when
// the live snapshot was sourced from the inverter (directly or merged in
// while the meter was active).
export type InverterExtras = {
  frequency?: number;
  dcPower?: number;
  inverterTemperature?: number;
  moduleTemperature?: number;
  reactivePower?: number;
  powerFactor?: PhaseType;
  dcInjection?: PhaseType;
  insulationResistance?: number;
  dcBusVoltage?: number;
  generationTimeToday?: number;
  generationTimeTotal?: number;
  energyProducedToday?: number;
};

export type LiveData = InverterExtras & {
  energyConsumed?: CalculatedEnergy;
  energyProduced: CalculatedEnergy;
  inductiveEnergyConsumed?: CalculatedEnergy;
  capacitiveEnergyProduced?: CalculatedEnergy;
  inductiveEnergyProduced?: CalculatedEnergy;
  capacitiveEnergyConsumed?: CalculatedEnergy;
  power: PhaseType;
  voltage: PhaseType;
  current: PhaseType;
  status: "producing" | "consuming" | "idle" | "offline";
  date: string;
  source?: RecordSource;
};

export const powerPlantStatusList = [
  "producing",
  "consuming",
  "idle",
  "offline",
] as const;

export type PowerPlantStatus = (typeof powerPlantStatusList)[number];

export type CalculatedEnergy = {
  day: number;
  month: number;
  year: number;
  /**
   * The plant's lifetime energy, summed across every source it has ever had.
   * **Not the device's counter** — since the backend learned to add up a
   * plant's whole history, these two parted company, and anything that
   * subtracts a stored record's counter from a live reading wants `counter`.
   */
  total: number;
  /**
   * The reading on the device right now, which is what a stored record holds.
   * Optional because a cached payload from before the split has only `total`,
   * where the two were the same thing.
   */
  counter?: number;
};

export type PhaseType = {
  L1: number;
  L2: number;
  L3: number;
};

export type LiveDataResponse = {
  liveData: LiveData;
  meterId: string;
  connectivityState?: "ok" | "pi_offline" | "device_offline" | null;
  connectivitySince?: string | null;
  lastContactAt?: string | null;
};

// What the navbar badge shows: the live production status, or one of the
// three "no data" verdicts. "stale" is the browser's own rule (live data
// older than a few polls) and is a softer state than the watchdog's two
// confirmed outages, which only appear after fifteen minutes of silence.
export type DisplayStatus = PowerPlantStatus | "offline" | "noReading" | "stale";

export function displayStatus(
  plant: (Pick<PowerPlant, "connectivityState"> & { offline?: boolean }) | undefined,
  liveData: LiveData | null | undefined,
): DisplayStatus {
  if (plant?.connectivityState === "pi_offline") return "offline";
  if (plant?.connectivityState === "device_offline") return "noReading";
  if (!liveData || plant?.offline) return "stale";
  return liveData.status;
}
